import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye } from 'lucide-react';
import { teamMembers, companyStats } from '../data/mock';
import { Button } from '../components/ui/button';

export default function AboutPage() {
  const introRef = useRef(null);
  const missionRef = useRef(null);
  const teamRef = useRef(null);
  const trustRef = useRef(null);

  const introInView = useInView(introRef, { once: true, margin: '-80px' });
  const missionInView = useInView(missionRef, { once: true, margin: '-80px' });
  const teamInView = useInView(teamRef, { once: true, margin: '-80px' });
  const trustInView = useInView(trustRef, { once: true, margin: '-80px' });

  return (
    <main className="w-full max-w-full overflow-x-hidden">
      {/* Page Hero */}
      <section className="relative overflow-hidden pt-32 sm:pt-36 pb-16 sm:pb-20">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[320px] sm:w-[500px] h-[280px] sm:h-[400px] bg-blue-600/6 rounded-full blur-[100px] sm:blur-[120px]" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3 block">
              About Us
            </span>

            <h1 className="text-[2.5rem] sm:text-5xl lg:text-6xl font-bold font-['Syne'] text-[var(--text-primary)] mb-6 leading-tight break-words">
              We&apos;re Your IT Solution.
              <br />
              <span className="text-blue-400">Digital-First</span> Agency.
            </h1>

            <p className="text-base sm:text-lg text-[var(--text-muted)] max-w-2xl leading-relaxed">
              Born from a passion for technology and design, Your IT Solution is a team of creatives, strategists, and engineers building digital solutions that drive real business growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Intro */}
      <section ref={introRef} className="relative py-20 bg-[var(--bg-soft)] overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={introInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-[var(--text-primary)] mb-6">
                Crafting Digital Excellence Since 2019
              </h2>

              <div className="space-y-4 text-[var(--text-muted)] leading-relaxed">
                <p>
                  Your IT Solution was founded with a simple mission: help businesses thrive in the digital world. Over the years, we&apos;ve grown from a small team of passionate developers into a full-service digital agency serving clients worldwide.
                </p>
                <p>
                  We believe great digital products are built at the intersection of stunning design, robust technology, and smart strategy. Every project we take on is an opportunity to create something remarkable.
                </p>
                <p>
                  Our approach is collaborative and transparent. We work closely with our clients, treating their goals as our own, and we don&apos;t stop until we&apos;ve exceeded expectations.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={introInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-2 gap-4 sm:gap-5"
            >
              {companyStats.map((stat) => (
                <div
                  key={stat.label}
                  className="p-5 sm:p-6 rounded-2xl bg-[var(--card-color)] border border-[var(--border-color)] min-w-0"
                >
                  <div className="text-2xl sm:text-3xl font-bold font-['Syne'] text-[var(--text-primary)] mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-[var(--text-muted)] break-words">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section ref={missionRef} className="relative py-24 overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={missionInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="p-6 sm:p-8 md:p-10 rounded-2xl bg-[var(--card-color)] border border-[var(--border-color)]"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-blue-400" />
              </div>

              <h3 className="text-2xl font-bold font-['Syne'] text-[var(--text-primary)] mb-4">
                Our Mission
              </h3>

              <p className="text-[var(--text-muted)] leading-relaxed">
                To empower businesses with innovative digital solutions that drive measurable growth. We aim to be the trusted partner that transforms ideas into impactful digital experiences, making cutting-edge technology accessible to businesses of all sizes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={missionInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="p-6 sm:p-8 md:p-10 rounded-2xl bg-[var(--card-color)] border border-[var(--border-color)]"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center mb-6">
                <Eye className="w-6 h-6 text-blue-400" />
              </div>

              <h3 className="text-2xl font-bold font-['Syne'] text-[var(--text-primary)] mb-4">
                Our Vision
              </h3>

              <p className="text-[var(--text-muted)] leading-relaxed">
                To become the go-to digital agency for brands seeking exceptional online presence. We envision a world where every business, regardless of size, can harness the full potential of digital to connect with their audience and grow sustainably.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section ref={teamRef} className="relative py-24 bg-[var(--bg-soft)] overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={teamInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3 block">
              Our Team
            </span>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Syne'] text-[var(--text-primary)] mb-5">
              Meet the People Behind Your IT Solution
            </h2>

            <p className="text-[var(--text-muted)] text-base sm:text-lg max-w-2xl mx-auto">
              A passionate team of designers, developers, and strategists dedicated to your success.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 30 }}
                animate={teamInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group text-center p-6 rounded-2xl bg-[var(--card-color)] border border-[var(--border-color)] hover:border-blue-500/20 transition-all duration-500 min-w-0"
              >
                <div className="w-24 h-24 mx-auto mb-5 rounded-full overflow-hidden ring-2 ring-[var(--border-color)] group-hover:ring-blue-500/30 transition-all duration-500">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>

                <h3 className="text-lg font-bold font-['Syne'] text-[var(--text-primary)] mb-1 break-words">
                  {member.name}
                </h3>

                <p className="text-blue-400 text-sm font-medium mb-3 break-words">
                  {member.role}
                </p>

                <p className="text-[var(--text-muted)] text-sm leading-relaxed break-words">
                  {member.bio}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section ref={trustRef} className="relative py-24 overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={trustInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3 block">
              Trust
            </span>

            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-[var(--text-primary)] mb-6">
              Why Clients Trust Us
            </h2>

            <p className="text-[var(--text-muted)] text-base sm:text-lg leading-relaxed mb-10">
              We don&apos;t just build digital products — we build partnerships. Our clients trust us because we deliver on our promises, communicate transparently, and always go the extra mile to ensure their success.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { value: '100%', label: 'Project Completion Rate' },
                { value: '98%', label: 'Client Satisfaction' },
                { value: '85%', label: 'Repeat Clients' },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={trustInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                  className="p-6 rounded-2xl bg-[var(--card-color)] border border-[var(--border-color)] min-w-0"
                >
                  <div className="text-3xl font-bold font-['Syne'] text-blue-400 mb-1">
                    {item.value}
                  </div>
                  <div className="text-sm text-[var(--text-muted)] break-words">
                    {item.label}
                  </div>
                </motion.div>
              ))}
            </div>

            <Link to="/contact" className="inline-block mt-12 w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-6 rounded-xl text-base transition-all duration-300 hover:shadow-lg hover:shadow-blue-600/20">
                Start a Project With Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}